import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth APIs
export const authAPI = {
  adminLogin: (adminId, password) =>
    api.post('/auth/admin/login', { adminId, password }),
  teacherLogin: (teacherId, password) =>
    api.post('/auth/teacher/login', { teacherId, password }),
};

// Teacher APIs
export const teacherAPI = {
  createTeacher: (data) => api.post('/teachers', data),
  getAllTeachers: () => api.get('/teachers'),
  getTeacher: (id) => api.get(`/teachers/${id}`),
  updateTeacher: (id, data) => api.put(`/teachers/${id}`, data),
  resetPassword: (id, newPassword) =>
    api.put(`/teachers/${id}/reset-password`, { newPassword }),
  deleteTeacher: (id) => api.delete(`/teachers/${id}`),
};

// Class APIs
export const classAPI = {
  createClass: (data) => api.post('/classes', data),
  getAllClasses: () => api.get('/classes'),
  getClass: (id) => api.get(`/classes/${id}`),
  updateClass: (id, data) => api.put(`/classes/${id}`, data),
  deleteClass: (id) => api.delete(`/classes/${id}`),
};

// Subject APIs
export const subjectAPI = {
  createSubject: (data) => api.post('/subjects', data),
  getAllSubjects: () => api.get('/subjects'),
  getSubjectsByClass: (classId) => api.get(`/subjects/class/${classId}`),
  getSubject: (id) => api.get(`/subjects/${id}`),
  updateSubject: (id, data) => api.put(`/subjects/${id}`, data),
  deleteSubject: (id) => api.delete(`/subjects/${id}`),
};

// Chapter APIs
export const chapterAPI = {
  createChapter: (data) => api.post('/chapters', data),
  getAllChapters: () => api.get('/chapters'),
  getChaptersBySubject: (subjectId) =>
    api.get(`/chapters/subject/${subjectId}`),
  getChapter: (id) => api.get(`/chapters/${id}`),
  updateChapter: (id, data) => api.put(`/chapters/${id}`, data),
  deleteChapter: (id) => api.delete(`/chapters/${id}`),
};

// Content APIs
export const contentAPI = {
  addContent: (formData) =>
    api.post('/content', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  getChapterContents: (chapterId) =>
    api.get(`/content/chapter/${chapterId}`),
  getContent: (id) => api.get(`/content/${id}`),
  updateContent: (id, formData) =>
    api.put(`/content/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteContent: (id) => api.delete(`/content/${id}`),
  deleteContentByType: (chapterId, type) =>
    api.delete(`/content/chapter/${chapterId}/type/${type}`),
};

// MCQ APIs
export const mcqAPI = {
  createMCQ: (data) => api.post('/mcq', data),
  getAllMCQs: () => api.get('/mcq'),
  getMCQ: (id) => api.get(`/mcq/${id}`),
  getMCQByChapter: (chapterId) =>
    api.get(`/mcq/chapter/${chapterId}`),
  getMCQByShareCode: (shareCode) =>
    api.get(`/mcq/public/${shareCode}`),
  updateMCQ: (id, data) => api.put(`/mcq/${id}`, data),
  togglePublish: (id) => api.patch(`/mcq/${id}/toggle-publish`),
  deleteMCQ: (id) => api.delete(`/mcq/${id}`),
};

// Student Response APIs
export const responseAPI = {
  submitResponse: (data) =>
    api.post('/responses/submit', data),
  getResponsesByMCQ: (mcqId) =>
    api.get(`/responses/mcq/${mcqId}`),
  getResponse: (id) => api.get(`/responses/${id}`),
  getResponsesByStudent: (mcqId, rollNumber) =>
    api.get(`/responses/mcq/${mcqId}/student/${rollNumber}`),
  getMCQStatistics: (mcqId) =>
    api.get(`/responses/mcq/${mcqId}/statistics`),
  deleteResponse: (id) => api.delete(`/responses/${id}`),
};

export default api;
